import leaflet from 'leaflet';

import {helper, http, api} from '../common/common';

class Boundary {
    constructor(viewStack, map, getAccessCode, onError) {
        this._viewStack = viewStack;
        this._map = map;
        this._getAccessCode = getAccessCode;
        this._onError = onError;
        this._inMarker = false;
        this._currentLayer = null;

        this._cancelToken = http.CancelToken.source();
    }

    unmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    update = async () => {
        try {
            const view = this._viewStack.peek();

            if (!view.organization) {
                await this._requestMapping(view);
            } else {
                await this._requestOrganizationZone(view, view.organization)
            }
        } catch (error) {
            const e = helper.sanitizeError(error, 'Boundary update failed', 'Unable to update boundary');
            this._processError(e);
        }
    }

    generateStyles = (theme) => {
        this._styleMap = {
            normal: {
                color: theme.palette.secondary.main,
                fillColor: theme.palette.common.transparent,
                weight: 2,
                dashArray: '0',
                opacity: 0.87,
                fillOpacity: 0.0,
            },
            selected: {
                color: theme.palette.common.red[500],
                fillColor: theme.palette.secondary.main,
                weight: 3,
                dashArray: '0',
                opacity: 1.0,
                fillOpacity: 0.5,
            },
            border: {
                color: theme.palette.primary.mainBorder,
                fillColor: theme.palette.common.transparent,
                weight: 2,
                dashArray: '0',
                opacity: 1.0,
                fillOpacity: 0.0,
            },
        };
    }

    insideMarker = () => {
        this._inMarker = true;
    }

    outsideMarker = () => {
        this._inMarker = false;

        if (this._currentLayer) {
            this._currentLayer.openTooltip();
            this._currentLayer.setStyle(this._styleMap.selected);
        }
    }

    pushIntoRegion = (key) => {
        const view = this._viewStack.peek();

        if (view.region.boundsObj) {
            const layerId = view.region.boundsObjKeyMap[key];

            const l = view.region.boundsObj.getLayer(layerId);

            if (l) {
                const bounds = l.getBounds();
                const latLngBounds = leaflet.latLngBounds(bounds);

                this._inMarker = false;
                this._currentLayer = null;

                this._viewStack.pushLayerObj(l, bounds, latLngBounds);
            }
        }
    }

    _borderStyle = (f) => {
        return this._styleMap.border;
    }

    _colorTable = (v) => {
        return v > 66 ? '#edf8b1' :
            v > 33  ? '#7fcdbb' : '#2c7fb8'
    }

    _featureStyle = (f) => {
        return this._styleMap.normal;
    }

    _featureEach = (f, l) => {
        l.bindTooltip(f.properties.name);

        l.on({
            mouseover: this._featureMouseOver,
            mouseout: this._featureMouseOut,
            click: this._featureSelect,
        });
    }

    _featureMouseOver = (e) => {
        const layer = e.target;

        this._currentLayer = layer;

        if (this._inMarker) {
            layer.closeTooltip();
        } else {
            layer.setStyle(this._styleMap.selected);
        }
    }

    _featureMouseOut = (e) => {
        const layer = e.target;

        if (this._currentLayer === layer) {
            this._currentLayer = null;
        }

        layer.setStyle(this._styleMap.normal);
    }

    _featureSelect = (e) => {
        const bounds = e.target.getBounds();
        const latLngBounds = leaflet.latLngBounds(bounds);

        this._inMarker = false;
        this._currentLayer = null;

        this._viewStack.pushLayerObj(e.target, bounds, latLngBounds);
    }

    _processError = (error) => {
        if (this._onError) {
            this._onError(error);
        }
    }

    _requestMapping = async (view) => {
        try {
            if (this._regionMap) {
                await this._requestOrganizationZone(view, '');
                return;
            }

            const response = await http.get(api.permission() + '/mapping', {
                params: {
                    'access_code': this._getAccessCode(),
                },
                cancelToken: this._cancelToken.token,
            });

            this._regionMap = response.data.key
            await this._requestOrganizationZone(view, '');
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            const e = helper.sanitizeError(error, 'Area Error', 'Unable to load area');
            this._processError(e);
        }
    }

    _requestOrganizationZone = async (view, organization) => {
        if (view.region.borderObj) {
            view.region.borderObj.addTo(this._map);
        } else {
            view.region.borderObj = leaflet.geoJson(null, {
                style: this._borderStyle,
                interactive: false,
            });
        }

        if (view.region.boundsObj) {
            view.region.boundsObj.addTo(this._map);
            return;
        } else {
            view.region.boundsObj = leaflet.geoJson(null, {
                style: this._featureStyle,
                onEachFeature: this._featureEach,
            });
        }

        view.region.featureData = [];
        view.region.borderObj.addTo(this._map);
        view.region.boundsObj.addTo(this._map);

        try {
            const requestList = [];

            if (organization) {
                requestList.push(this._requestSingleZone(view, organization));
            } else {
                for (const region of this._regionMap) {
                    requestList.push(this._requestSingleZone(view, region))
                }
            }

            await Promise.all(requestList);

            view.region.boundsObjKeyMap = {};

            view.region.boundsObj.eachLayer((l) => {
                const key = l.feature.properties.key;
                const layerId = view.region.boundsObj.getLayerId(l);
                view.region.boundsObjKeyMap[key] = layerId;
            });

            view.loaded = Date.now();
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            const e = helper.sanitizeError(error, 'Boundary Error', 'Unable to aggregrate boundary');
            this._processError(e);
        }
    }

    _requestSingleZone = async (view, organization) => {
        try {
            view.region.borderObj.clearLayers();

            if (view.region.borderData) {
                view.region.borderObj.addData(view.region.borderData);
                view.region.borderObj.addTo(this._map);
            } else {
                view.region.borderObj.remove();
            }

            const response = await http.get(api.map() + '/boundary', {
                params: {
                    'access_code': this._getAccessCode(),
                    'organization': organization,
                    'region': view.key,
                },
                cancelToken: this._cancelToken.token,
                cacheKey: 'boundary/' + organization + '/' + view.key,
            });

            if (Object.entries(response.data.content).length) {
                for (const f of response.data.content.features) {
                    f.properties.organization = organization;
                    view.region.featureData.push(f);
                }

                view.region.boundsObj.addData(response.data.content);
            }
        } catch (error) {
            if (http.isCancel(error)) {
                throw error;
            }

            const e = helper.sanitizeError(error, 'Boundary Error', 'Unable to load boundary');
            this._processError(e);
        }
    }
}

export default Boundary;
